import { useAddOrderLineMutation } from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import getErrorOutDated from "@saleor/utils/errors/outDated";
import { FormikProps } from "formik";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { NotRefOrderSortField } from "../urls";
import { orderLinesMapping } from "../utils";

interface Props {
  formikItemDetails: FormikProps<{ lines: NotRefOrderLine[] }>;
  formikHeader: FormikProps<NotRefOrderHeader>;
  searchModal: any;
  sort: any;
  handleCloseSearchModal: () => void;
  handleSort: (field: any) => void;
  setTableLoading: (loading: boolean) => void;
  setSubmitState: Dispatch<SetStateAction<any>>;
  totalItemsOriginal?: number;
}

function useAddOrderLine({
  formikItemDetails,
  formikHeader,
  searchModal,
  sort,
  handleCloseSearchModal,
  handleSort,
  setTableLoading,
  setSubmitState,
  totalItemsOriginal
}: Props) {
  const [addOrderLine] = useAddOrderLineMutation();
  const notifer = useNotifier();

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (isAddSuccess && sort.asc) {
      handleSort(NotRefOrderSortField.itemNo);
    }
  }, [isAddSuccess]);

  const getUpdatedLinesWithHeader = (lines, overrideData?: {}) =>
    lines.map(line => {
      if (!line.parentItemNo) {
        return {
          ...line,
          requestDate: formikHeader?.values?.requestDate,
          priceDate: formikHeader?.values?.requestDate,
          ...overrideData
        };
      }
      return line;
    });

  const updateItemNumbers = (prev, newLines, i, deleteCount) => {
    prev[i].itemNo = String(
      +prev[i].itemNo + (newLines.length - deleteCount) * 10
    );
    if (prev[i].parentItemNo) {
      prev[i].parentItemNo = String(
        +prev[i].parentItemNo + (newLines.length - deleteCount) * 10
      );
    }
  };

  const getDeleteCount = (prev, index, prevDeleteCount, asc) => {
    let deleteCount = prevDeleteCount;

    const currentItem = prev[index];

    if (!currentItem.bomFlag || currentItem.parentItemNo) {
      return deleteCount;
    }

    const incrementDeleteCount = () => {
      deleteCount++;
    };

    const matchesParentItemNo = i =>
      prev[i]?.parentItemNo === currentItem.itemNo;

    const shouldContinue = i => (asc ? i < prev.length : i >= 0);

    for (
      let i = asc ? index + 1 : index - 1;
      shouldContinue(i);
      asc ? i++ : i--
    ) {
      if (matchesParentItemNo(i)) {
        incrementDeleteCount();
      } else {
        break;
      }
    }

    return deleteCount;
  };

  const handleAddToChangeOrder = materialCode => {
    setTableLoading(true);
    handleCloseSearchModal();
    addOrderLine({
      variables: {
        id: searchModal.lineId,
        orderId: formikHeader.values?.id,
        materialCode,
        totalItems: totalItemsOriginal
      }
    })
      .then(({ data }) => {
        if (data?.addOrderLine?.errors?.length) {
          const addOrderLine = data?.addOrderLine;
          const errorOutDated = getErrorOutDated(addOrderLine);
          if (errorOutDated) {
            setSubmitState({
              state: "errorPopup",
              context: errorOutDated
            });
          } else {
            notifer({
              status: "error",
              apiMessage: addOrderLine?.errors[0].message
            });
          }
        }
        if (data?.addOrderLine?.orderLines) {
          const index = +searchModal.id / 10 - 1;
          const prev: NotRefOrderLine[] = [...formikItemDetails.values.lines];
          const afterMapping = orderLinesMapping(
            data?.addOrderLine?.orderLines,
            {
              poNumber: null
            }
          );
          let newLines = getUpdatedLinesWithHeader(afterMapping, {
            requestDate: ""
          });
          newLines = newLines.map(item => ({ ...item, isNewItem: true }));
          if (sort.asc) {
            prev.splice(index + 1, 0, ...newLines);
          } else {
            newLines.sort(
              (a, b) => parseInt(b.itemNo, 10) - parseInt(a.itemNo, 10)
            );
            prev.splice(0, 0, ...newLines);
          }

          formikItemDetails.setFieldValue("lines", prev, true);
          setIsAddSuccess(true);
        }
      })
      .finally(() => {
        setIsAddSuccess(false);
        setTableLoading(false);
      });
  };

  const handleAddToOrder = materialCode => {
    setTableLoading(true);
    handleCloseSearchModal();
    addOrderLine({
      variables: {
        id: searchModal.lineId,
        orderId: formikHeader.values?.id,
        materialCode
      }
    })
      .then(({ data }) => {
        if (data?.addOrderLine?.errors?.length) {
          notifer({
            status: "error",
            apiMessage: data?.addOrderLine.errors[0].message
          });
        }
        if (data?.addOrderLine?.orderLines) {
          let index = +searchModal.id / 10 - 1;
          const prev: NotRefOrderLine[] = [...formikItemDetails.values.lines];
          const afterMapping = orderLinesMapping(
            data?.addOrderLine?.orderLines,
            {
              poNumber: null
            }
          );
          let newLines = getUpdatedLinesWithHeader(afterMapping);
          let deleteCount = 1;
          if (index < 0 && index > prev.length) {
            return;
          }
          if (sort.asc) {
            deleteCount = getDeleteCount(prev, index, deleteCount, sort.asc);
            prev.splice(index, deleteCount, ...newLines);
            if (
              data?.addOrderLine?.itemNoUpdated &&
              index + newLines.length <= prev.length
            ) {
              for (let i = index + newLines.length; i < prev.length; i++) {
                updateItemNumbers(prev, newLines, i, deleteCount);
              }
            }
          } else {
            index = prev.length - index - 1;
            newLines = newLines.reverse();
            deleteCount = getDeleteCount(prev, index, deleteCount, sort.asc);
            prev.splice(index - deleteCount + 1, deleteCount, ...newLines);
            if (data?.addOrderLine?.itemNoUpdated && index - deleteCount >= 0) {
              for (let i = index - deleteCount; i >= 0; i--) {
                updateItemNumbers(prev, newLines, i, deleteCount);
              }
            }
          }
          formikItemDetails.setFieldValue("lines", prev, true);
        }
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  return { handleAddToOrder, handleAddToChangeOrder };
}

export default useAddOrderLine;
